import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __unpauseTmobilej } from "../../../../api/sim";
import { __fetchPlans } from "../../../../api/plan";

const TmobilejUnpauseForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [plans, setPlans] = useState([]);
  const { setAlertMessage } = useAlert();
  const initialData = { lines: [], plan_id: null };
  const [formData, setFormData] = useState(null);
  const [response, call, loading] = useAxios({ ...__unpauseTmobilej });
  const [planResponse, planCall] = useAxios({ ...__fetchPlans });

  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    msisdn: yup
      .string("msisdn")
      .min(10, "Should be exactly 10 digits")
      .max(10, "Should be exactly 10 digits")
      .matches(/^\d+$/, "Must be digits only")
      .required(),
  });

  const validationSchema = yup.object({
    plan_id: yup
      .number("plan_id")
      .required("Plan is required")
      .nullable(),
  });

  useEffect(() => {
    planCall({ params: { page_size: 100 } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (planResponse && planResponse.status) {
      setPlans(planResponse.data.data || planResponse.data);
    }
  }, [planResponse]);

  useEffect(() => {
    if (formData && formData.lines?.length) {
      const plan = plans.find((p) => p.id === formData.plan_id);
      call({
        data: {
          lines: formData.lines,
          plan_id: formData.plan_id,
          plan_name: plan?.plan_name || plan?.name || null,
        },
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({
        type: "success",
        message: "Un-suspend (unpause) queue started",
      });
      setLinesData([]);
      nav("/tmobilej/unpause");
    }
    formik.setSubmitting(false);
  }, [response]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];

    if (!data) {
      return errors;
    }

    const lines = data.split("\n");
    lines.some((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return false;

      const parts = trimmed.split(",");
      const msisdn = (parts[0] || "").trim().replace(/^\+1/, "");
      if (msisdn.length !== 10 || !/^\d+$/.test(msisdn)) {
        errors.lines = `Invalid MSISDN in line ${idx + 1}: ${line}`;
        return true;
      }

      try {
        lineValidationSchema.validateSync({ msisdn });
        formatedLines.push({ msisdn });
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }
      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    validationSchema,
    onSubmit: (values) => {
      const parsed = [];
      const lines = rawData.split("\n");
      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (!trimmed) continue;
        const msisdn = trimmed.split(",")[0].trim().replace(/^\+1/, "");
        if (msisdn.length !== 10 || !/^\d+$/.test(msisdn)) continue;
        parsed.push({ msisdn });
      }
      if (parsed.length) {
        setFormData({ lines: parsed, plan_id: values.plan_id });
      }
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();
    reader.onload = async ({ target }) => {
      setRawData(target.result);
      const lineErrors = validateFunction(target.result);
      formik.setErrors({ ...formik.errors, lines: lineErrors.lines });
    };
    reader.readAsText(inputFile);
  };

  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];
    if (e.target.files.length) {
      const inputFile = e.target.files[0];
      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors,
          upload: "Please input a csv file",
        });
        return;
      }
      handleParse(inputFile);
    }
  };

  const canSubmit =
    !formik.errors.lines &&
    linesData.length > 0 &&
    Boolean(formik.values.plan_id) &&
    !formik.errors.plan_id;

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Un-suspend (Unpause)"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} minWidth={350}>
            <FormControl
              error={
                formik.touched.plan_id && Boolean(formik.errors.plan_id)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputFieldAutoComplete
                isSelect
                options={[
                  ...plans.reduce((acc, plan) => {
                    const val = {
                      value: plan.id,
                      label: plan.plan_name || plan.name,
                    };
                    acc.push(val);
                    return acc;
                  }, []),
                ]}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                name="plan_id"
                label="Plan"
                id="plan_id"
                onChange={(e, value) => {
                  formik.setFieldValue("plan_id", value?.value || null);
                }}
              />
              {formik.touched.plan_id && Boolean(formik.errors.plan_id) ? (
                <FormHelperText id="plan_id-error-text">
                  {formik.errors.plan_id}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload)}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) && (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              Add phone numbers (10-digit MSISDN, one per line)
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.lines)}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder="1234567890\n9876543210"
                style={{ minWidth: 550 }}
                minRows={5}
                name="lines"
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  const lineErrors = validateFunction(data);
                  formik.setErrors({
                    ...formik.errors,
                    lines: lineErrors.lines,
                  });
                }}
              />
              {Boolean(formik.errors.lines) && (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={!canSubmit}
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                Un-suspend
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejUnpauseForm;
